import { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HardHat, ArrowRight, Briefcase, User } from "lucide-react";
import { COUNTIES } from "@/constants/data";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const defaultRole = params.get("role") === "employer" ? "employer" : "worker";

  const [role, setRole] = useState(defaultRole);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    county: "Dublin",
    referral_code: (params.get("ref") || "").toUpperCase(),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register({ ...form, role });
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome ${res.user.name}, your account is live`);
      // If they came from the Founding Member banner, take them straight to checkout
      if (params.get("founding") === "1") {
        try {
          const { data } = await api.post("/payments/checkout", {
            lookup_key: "founding_member",
            origin_url: window.location.origin,
          });
          window.location.href = data.checkout_url;
          return;
        } catch {
          // fall through to dashboard
        }
      }
      navigate("/dashboard");
    } else {
      setError(res.error);
    }
  };

  const roleTiles = useMemo(() => ([
    { key: "employer", Icon: Briefcase, title: "I Hire Crew", sub: "Post shifts, find workers." },
    { key: "worker", Icon: User, title: "I Am a Tradesperson", sub: "List availability, get work." },
  ]), []);

  return (
    <div className="min-h-[calc(100vh-4rem)] max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-16">
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 bg-amber-500 grid place-items-center">
            <HardHat className="w-5 h-5 text-neutral-950" strokeWidth={2.5} />
          </div>
          <div className="display text-2xl">SITE<span className="text-amber-500">CREWS</span></div>
        </div>
        <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Sign Up</div>
        <h1 className="display text-5xl md:text-6xl mt-2">Get on the site.</h1>
        <p className="text-neutral-400 mt-3 max-w-md">Free to browse. Direct contact once you subscribe — no agency fees, no middlemen. Workers €9/mo · Employers €49/mo.</p>
      </div>

      <form onSubmit={onSubmit} className="mt-10 grid md:grid-cols-2 gap-10" data-testid="register-form">
        <div className="space-y-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-3">I am a…</label>
            <div className="grid grid-cols-2 gap-px bg-neutral-800 border border-neutral-800">
              {roleTiles.map(({ key, Icon, title, sub }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`role-${key}`}
                  onClick={() => setRole(key)}
                  className={`p-5 text-left bg-neutral-950 transition-colors ${role === key ? "bg-amber-500 text-neutral-950" : "hover:bg-neutral-900 text-neutral-200"}`}
                >
                  <Icon className={`w-6 h-6 mb-3 ${role === key ? "text-neutral-950" : "text-amber-500"}`} strokeWidth={2.5} />
                  <div className="display text-xl">{title}</div>
                  <div className={`text-xs uppercase tracking-wide mt-1 ${role === key ? "text-neutral-800" : "text-neutral-400"}`}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Full Name</label>
            <input data-testid="reg-name-input" required value={form.name} onChange={setField("name")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="John O'Brien" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Email</label>
            <input data-testid="reg-email-input" required type="email" value={form.email} onChange={setField("email")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="you@example.ie" />
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Phone (revealed on apply)</label>
            <input data-testid="reg-phone-input" value={form.phone} onChange={setField("phone")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="+353 ..." />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">County</label>
            <select data-testid="reg-county-select" value={form.county} onChange={setField("county")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
              {COUNTIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2">Password (min 6)</label>
            <input data-testid="reg-password-input" required type="password" minLength={6} value={form.password} onChange={setField("password")} className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="••••••••" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-neutral-400 block mb-2 flex items-center gap-2">
              Referral code <span className="text-neutral-600 normal-case tracking-normal text-[10px]">(optional · saves you €5)</span>
            </label>
            <input
              data-testid="reg-referral-input"
              value={form.referral_code}
              onChange={(e) => setForm((f) => ({ ...f, referral_code: e.target.value.toUpperCase() }))}
              className="w-full bg-neutral-900 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3 font-mono tracking-widest"
              placeholder="PADDY-A3F9"
            />
          </div>

          {error && <div data-testid="register-error" className="border border-red-800 bg-red-950/40 text-red-300 px-4 py-3 text-sm">{error}</div>}

          <button
            data-testid="register-submit-btn"
            disabled={loading}
            className="w-full bg-amber-500 text-neutral-950 font-bold uppercase tracking-widest py-3 hover:bg-amber-400 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? "Creating…" : (<>Create Account <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <div className="text-sm text-neutral-400 text-center">
            Already have one?{" "}
            <Link data-testid="switch-to-login" to="/login" className="text-amber-500 hover:underline">Log in</Link>
          </div>
        </div>
      </form>
    </div>
  );
}
